import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { Warning, CheckCircle, Cancel } from "@mui/icons-material";

const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  type = "finish", // 'finish', 'unattempted', 'proceed', 'timeExpired'
  timeRemaining = null,
  unattemptedCount = 0,
}) => {
  const getDialogContent = () => {
    switch (type) {
      case "finish":
        return {
          title: "Confirm Section Completion",
          icon: <Warning color="warning" sx={{ fontSize: 40 }} />,
          content: (
            <Box>
              {timeRemaining && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Time Remaining:</strong> You still have time left in
                    this section.
                  </Typography>
                </Alert>
              )}
              <Typography variant="body1" paragraph>
                If you choose to end this section now, you will not be able to
                go back and make any changes.
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                Are you sure you want to finish this section?
              </Typography>
            </Box>
          ),
          confirmText: "Yes, finish this section",
          cancelText: "No, take me back to the section",
          confirmColor: "success",
        };

      case "unattempted":
        return {
          title: "Unattempted Questions – Confirm Section Completion",
          icon: <Warning color="warning" sx={{ fontSize: 40 }} />,
          content: (
            <Box>
              <Typography variant="body1" paragraph>
                You still have time remaining, and some questions have not been
                attempted.
              </Typography>
              <Typography variant="body1" paragraph>
                If you choose to finish this section now, you will not be able
                to return to complete the unanswered questions.
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                Are you sure you want to finish this section?
              </Typography>
            </Box>
          ),
          confirmText: "Yes, finish this section",
          cancelText: "No, go back to complete the section",
          confirmColor: "warning",
        };

      case "finalSubmit":
        return {
          title: "Final Submission Confirmation",
          icon: <Warning color="error" sx={{ fontSize: 40 }} />,
          content: (
            <Box>
              <Typography variant="body1" paragraph>
                If you select Finish Section, your answers will be submitted,
                and you will not be able to return to this section.
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                Please confirm your action:
              </Typography>
            </Box>
          ),
          confirmText: "Finish Section",
          cancelText: "Cancel",
          confirmColor: "error",
        };

      case "proceed":
        return {
          title: "Confirmation Required",
          icon: <Warning color="warning" sx={{ fontSize: 40 }} />,
          content: (
            <Box>
              <Typography variant="body1" paragraph fontWeight="bold">
                Are you sure you want to proceed?
              </Typography>
              <Typography variant="body1" paragraph>
                You have not attempted this question.
              </Typography>
              <Typography variant="body1" paragraph>
                If you continue, your response will be left blank.
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                Do you still want to move forward?
              </Typography>
            </Box>
          ),
          confirmText: "Yes",
          cancelText: "No",
          confirmColor: "warning",
        };

      case "timeExpired":
        return {
          title: "Time Expired",
          icon: <Warning color="error" sx={{ fontSize: 40 }} />,
          content: (
            <Box>
              <Typography variant="body1" paragraph>
                The timer for the previous section has ended.
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                You are now being directed to the next section.
              </Typography>
            </Box>
          ),
          confirmText: "Close",
          cancelText: null,
          confirmColor: "primary",
        };

      default:
        return {
          title: "Confirmation",
          icon: <CheckCircle color="primary" sx={{ fontSize: 40 }} />,
          content: <Typography>Please confirm your action.</Typography>,
          confirmText: "Confirm",
          cancelText: "Cancel",
          confirmColor: "primary",
        };
    }
  };

  const { title, icon, content, confirmText, cancelText, confirmColor } =
    getDialogContent();

  return (
    <Dialog
      open={open}
      onClose={undefined} // Disable click-outside-to-close - user must click button
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={true} // Disable ESC key to close
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {icon}
          <Typography variant="h6" fontWeight="bold">
            {title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 3 }}>{content}</DialogContent>
      <DialogActions sx={{ p: 3, gap: 2, justifyContent: "center" }}>
        {cancelText && (
          <Button
            onClick={onClose}
            variant="outlined"
            startIcon={<Cancel />}
            sx={{ minWidth: 150 }}
          >
            {cancelText}
          </Button>
        )}
        <Button
          onClick={onConfirm}
          variant="contained"
          color={confirmColor}
          startIcon={<CheckCircle />}
          sx={{ minWidth: 150 }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
